import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, ArrowLeft, Clock, Star, Loader, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JeopardyGameViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameState, setGameState] = useState('board'); // board, question, results
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState([]); // Track answered (both correct and incorrect)
  const [incorrectQuestions, setIncorrectQuestions] = useState([]); // Track incorrect answers specifically
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [timer, setTimer] = useState(20);
  const [timerInterval, setTimerInterval] = useState(null);
  const [grading, setGrading] = useState(false);
  const [questionResult, setQuestionResult] = useState(null);
  const [lastScores, setLastScores] = useState([]);

  useEffect(() => {
    loadGame();
    loadLastScores();
  }, [id]);

  useEffect(() => {
    if (timer === 0 && gameState === 'question') {
      handleTimeUp();
    }
  }, [timer]);

  const loadGame = async () => {
    try {
      const res = await axios.get(`${API}/jeopardy-games/${id}`);
      setGame(res.data);
    } catch (error) {
      console.error('Failed to load Jeopardy game:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLastScores = async () => {
    try {
      const res = await axios.get(`${API}/game-scores/jeopardy_game/${id}`);
      setLastScores(res.data);
    } catch (error) {
      console.error('Failed to load scores:', error);
    }
  };

  const handleQuestionClick = (questionIdx) => {
    if (answeredQuestions.includes(questionIdx)) return;
    
    setSelectedQuestion(questionIdx);
    setGameState('question');
    setCurrentAnswer('');
    setQuestionResult(null);
    setTimer(20);

    const q = game.questions[questionIdx];
    if (q.options && q.options.length > 0) {
      setShuffledOptions([...q.options].sort(() => Math.random() - 0.5));
    } else {
      setShuffledOptions([]);
    }
    
    // Start countdown
    const interval = setInterval(() => {
      setTimer(prev => prev - 1);
    }, 1000);
    setTimerInterval(interval);
  };

  const handleTimeUp = () => {
    if (timerInterval) clearInterval(timerInterval);
    setQuestionResult({
      is_correct: false,
      points_awarded: 0,
      feedback: 'Time\'s up! You didn\'t answer in time.'
    });
  };

  const handleSubmitAnswer = async (answerOverride) => {
    const answerToSubmit = answerOverride !== undefined ? answerOverride : currentAnswer;
    if (!answerToSubmit.trim()) return;
    if (timerInterval) clearInterval(timerInterval);
    setCurrentAnswer(answerToSubmit);
    
    setGrading(true);
    try {
      const res = await axios.post(`${API}/jeopardy-games/${id}/grade-answer`, {
        question_index: selectedQuestion,
        user_answer: answerToSubmit
      });
      
      setQuestionResult(res.data);
      
      // Add to answered questions (both correct and incorrect)
      setAnsweredQuestions([...answeredQuestions, selectedQuestion]);
      
      // Track incorrect answers separately
      if (!res.data.is_correct) {
        setIncorrectQuestions([...incorrectQuestions, selectedQuestion]);
      } else {
        // Only add score if correct
        setTotalScore(totalScore + (res.data.points_awarded || 0));
      }
    } catch (error) {
      console.error('Failed to grade answer:', error);
      alert('Failed to grade answer. Please try again.');
    } finally {
      setGrading(false);
    }
  };

  const handleNextQuestion = () => {
    setGameState('board');
    setSelectedQuestion(null);
    setQuestionResult(null);
    
    // Check if game is complete
    if (answeredQuestions.length >= game.questions.length - 1) {
      handleGameComplete();
    }
  };

  const handleGameComplete = async () => {
    setGameState('results');
    
    // Save score
    try {
      await axios.post(`${API}/game-scores`, {
        game_type: 'jeopardy_game',
        game_id: id,
        score: totalScore
      });
      loadLastScores();
    } catch (error) {
      console.error('Failed to save score:', error);
    }
  };

  const resetGame = () => {
    setGameState('board');
    setSelectedQuestion(null);
    setAnsweredQuestions([]);
    setCurrentAnswer('');
    setTotalScore(0);
    setTimer(20);
    setQuestionResult(null);
    if (timerInterval) clearInterval(timerInterval);
  };

  const getQuestionsByCategory = (category) => {
    return game.questions
      .map((q, idx) => ({ ...q, index: idx }))
      .filter(q => q.category === category)
      .sort((a, b) => a.value - b.value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFA500] mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!game) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-xl text-red-600">Jeopardy game not found</p>
        </div>
      </Layout>
    );
  }

  const currentQuestion = selectedQuestion !== null ? game.questions[selectedQuestion] : null;

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate('/jeopardy-center')}
          className="flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Jeopardy Center</span>
        </button>

        <div className="bg-gradient-to-r from-orange-500 to-yellow-600 text-white p-8 rounded-lg mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Trophy className="w-12 h-12" />
              <div>
                <h1 className="text-4xl font-bold retro-title">{game.test_title}</h1>
                <p className="text-lg opacity-90">Jeopardy Game</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold">{totalScore}</div>
              <div className="text-sm opacity-90">Points</div>
            </div>
          </div>
        </div>

        {/* Game Board */}
        {gameState === 'board' && (
          <div className="overflow-x-auto">
            <div style={{ minWidth: `${game.game_categories.length * 180}px` }}>
              {/* Category Headers */}
              <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: `repeat(${game.game_categories.length}, minmax(0, 1fr))` }}>
                {game.game_categories.map((category, idx) => (
                  <div key={idx} className="bg-blue-900 text-white p-4 text-center rounded-t-lg">
                    <h3 className="text-lg font-bold uppercase">{category}</h3>
                  </div>
                ))}
              </div>

              {/* Question Values */}
              {[100, 200, 300, 400, 500].map(value => (
                <div key={value} className="grid gap-2 mb-2" style={{ gridTemplateColumns: `repeat(${game.game_categories.length}, minmax(0, 1fr))` }}>
                  {game.game_categories.map(category => {
                    const questions = getQuestionsByCategory(category);
                    const question = questions.find(q => q.value === value);
                    const isAnswered = question && answeredQuestions.includes(question.index);

                    return (
                      <button
                        key={`${category}-${value}`}
                        onClick={() => question && handleQuestionClick(question.index)}
                        disabled={!question || isAnswered}
                        className={`h-24 text-3xl font-bold rounded-lg transition-all transform hover:scale-105 ${
                          isAnswered
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white hover:shadow-xl'
                        }`}
                      >
                        {isAnswered ? '—' : `$${value}`}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Question View */}
        {gameState === 'question' && currentQuestion && (
          <div className="max-w-3xl mx-auto">
            {/* Timer */}
            <div className="bg-white border-4 border-orange-500 p-6 rounded-lg mb-6 text-center">
              <Clock className="w-12 h-12 text-orange-500 mx-auto mb-2" />
              <div className={`text-6xl font-bold ${timer <= 5 ? 'text-red-600 animate-pulse' : 'text-gray-800'}`}>
                {timer}
              </div>
              <div className="text-sm text-gray-600">seconds remaining</div>
            </div>

            {/* Question Card */}
            <div className="bg-blue-900 text-white p-8 rounded-lg mb-6">
              <div className="text-center mb-4">
                <div className="text-sm opacity-75 mb-2">{currentQuestion.category}</div>
                <div className="text-5xl font-bold text-yellow-400">${currentQuestion.value}</div>
              </div>
              <div className="bg-white text-blue-900 p-6 rounded-lg">
                <p className="text-2xl font-semibold text-center">{currentQuestion.answer}</p>
              </div>
            </div>

            {/* Answer Input or Result */}
            {!questionResult ? (
              currentQuestion.options && currentQuestion.options.length > 0 ? (
                <div className="space-y-3" data-testid="jeopardy-mc-options">
                  <label className="block text-lg font-bold text-gray-800 mb-2 text-center">
                    Choose the correct response:
                  </label>
                  {shuffledOptions.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleSubmitAnswer(option)}
                      disabled={grading}
                      className="w-full text-left bg-white border-4 border-orange-400 hover:bg-orange-50 hover:border-orange-500 disabled:opacity-50 p-4 rounded-lg text-lg font-semibold text-gray-800 transition-all"
                      data-testid={`jeopardy-option-${oIndex}`}
                    >
                      {option}
                    </button>
                  ))}
                  {grading && (
                    <div className="flex items-center justify-center gap-2 text-orange-600 font-semibold">
                      <Loader className="w-5 h-5 animate-spin" /> Checking...
                    </div>
                  )}
                </div>
              ) : (
              <div>
                <label className="block text-lg font-bold text-gray-800 mb-2">
                  Remember: Answer in question form! (e.g., "What is...?" "Who is...?")
                </label>
                <input
                  type="text"
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSubmitAnswer()}
                  disabled={grading}
                  className="w-full border-4 border-orange-500 p-4 rounded-lg text-xl mb-4 focus:outline-none focus:border-orange-600"
                  placeholder="Type your answer here..."
                  autoFocus
                />
                <button
                  onClick={() => handleSubmitAnswer()}
                  disabled={grading || !currentAnswer.trim()}
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-600 text-white px-8 py-4 rounded-lg font-bold text-xl hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {grading ? (
                    <>
                      <Loader className="w-6 h-6 animate-spin" />
                      Ernie.AI is grading...
                    </>
                  ) : (
                    'Submit Answer'
                  )}
                </button>
              </div>
              )
            ) : (
              <div className={`p-6 rounded-lg border-4 ${
                questionResult.is_correct ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  {questionResult.is_correct ? (
                    <>
                      <CheckCircle className="w-10 h-10 text-green-600" />
                      <div>
                        <div className="text-3xl font-bold text-green-600">Correct!</div>
                        <div className="text-xl text-green-700">+${questionResult.points_awarded} points</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-10 h-10 text-red-600" />
                      <div className="text-3xl font-bold text-red-600">Incorrect</div>
                    </>
                  )}
                </div>
                <div className="mb-4">
                  <span className="font-semibold">Correct Answer: </span>
                  <span className="text-gray-700">{currentQuestion.question}</span>
                </div>
                <div className="mb-4">
                  <span className="font-semibold">Your Answer: </span>
                  <span className="text-gray-700">{currentAnswer}</span>
                </div>
                <div className="mb-4 p-3 bg-white rounded">
                  <span className="font-semibold block mb-1">Ernie.AI Feedback:</span>
                  <p className="text-gray-700">{questionResult.feedback}</p>
                </div>
                <button
                  onClick={handleNextQuestion}
                  className="w-full bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-all"
                >
                  {answeredQuestions.length >= game.questions.length - 1 ? 'See Final Results' : 'Next Question'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Results Screen */}
        {gameState === 'results' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white p-8 rounded-lg text-center mb-8">
              <Trophy className="w-20 h-20 mx-auto mb-4" />
              <h2 className="text-5xl font-bold retro-title mb-4">Game Complete!</h2>
              <div className="text-7xl font-bold mb-2">{totalScore}</div>
              <div className="text-2xl">Total Points</div>
            </div>

            <div className="bg-white border-4 border-orange-500 p-6 rounded-lg mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-orange-500" />
                Your Performance
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-800">{answeredQuestions.length}</div>
                  <div className="text-sm text-gray-600">Questions Answered</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600">{Math.round((totalScore / 3000) * 100)}%</div>
                  <div className="text-sm text-gray-600">Score Percentage</div>
                </div>
              </div>
              <button
                onClick={resetGame}
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-600 text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition-all"
              >
                Play Again
              </button>
            </div>

            {/* Last 3 Scores */}
            {lastScores.length > 0 && (
              <div className="bg-white border-4 border-orange-500 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-orange-500" />
                  Your Last 3 Scores
                </h3>
                <div className="space-y-2">
                  {lastScores.map((score, idx) => (
                    <div key={score.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                      <span className="font-semibold text-gray-700">Attempt {idx + 1}</span>
                      <span className="text-orange-600 font-bold text-xl">{score.score} points</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
